/* eslint-disable no-case-declarations */
import { ConditionType } from '../../enums/condition-type';
import { NodeTypes } from '../../enums/node-types';
import { Node, NodeCondition } from '../../models/base';
import { NormalizedEntity } from '../../models/contentful-mapped';

export class LayoutUtils {
  static gridNodes = [NodeTypes.ROW, NodeTypes.COLUMN, NodeTypes.NESTED_COLUMN];

  static nonElementNodes = [
    NodeTypes.SECTION,
    NodeTypes.ROW,
    NodeTypes.COLUMN,
    NodeTypes.NESTED_COLUMN,
    NodeTypes.GLOBAL_SECTION,
    NodeTypes.DYNAMIC_DATA_CONTENT
  ];

  static containerNodeTypes = [
    NodeTypes.ROW,
    NodeTypes.NESTED_ROW,
    NodeTypes.ROWS_AS_TABS,
    NodeTypes.COLUMN,
    NodeTypes.NESTED_COLUMN,
    NodeTypes.SECTION,
    NodeTypes.GLOBAL_SECTION
  ];

  static fullHeightNodes = [NodeTypes.COLUMN, NodeTypes.NESTED_COLUMN];

  static fullWidthNodes = [
    NodeTypes.ADDRESS_MAP,
    NodeTypes.BLOG_HEADER,
    NodeTypes.BLOG_OVERVIEW,
    NodeTypes.CALL_OFF_ORDER_DETAILS,
    NodeTypes.MARKET_ITEM_LIST,
    NodeTypes.ORDER_DETAILS,
    NodeTypes.ORDER_OVERVIEW,
    NodeTypes.INVOICES,
    NodeTypes.PRODUCT_LISTER_BLOCK,
    NodeTypes.PRODUCT_PORTFOLIO,
    NodeTypes.QUICK_TDS_LINK,
    NodeTypes.TECHNOLOGY_ITEM_LIST,
    NodeTypes.TEXT_ACCORDION,
    NodeTypes.BRAND_LIST,
    NodeTypes.VAT_NUMBER_LIST,
    NodeTypes.CASE_STUDY_LIST,
    NodeTypes.FORM_EMBED,
    NodeTypes.IMAGE_GALLERY,
    NodeTypes.MARKET_LIST,
    NodeTypes.MARKET_SEGMENT_LIST,
    NodeTypes.MARKET_TILE_LIST,
    NodeTypes.PRODUCER_LIST,
    NodeTypes.PRODUCT_LIST,
    NodeTypes.RELATED_DOCUMENTS,
    NodeTypes.SHIPMENT_DETAILS,
    NodeTypes.TECHNOLOGY_TILE_LIST,
    NodeTypes.TILE,
    NodeTypes.DETAIL_HEADER,
    NodeTypes.DIVIDER,
    NodeTypes.IMAGE_CAROUSEL,
    NodeTypes.EMAIL_UNSUBSCRIBE,
    NodeTypes.EXTENDED_TECHNICAL_PROPERTIES,
    NodeTypes.HOMEPAGE_SEARCH,
    NodeTypes.PRODUCT_TABLE,
    NodeTypes.REQUEST_FOR_QUOTATION,
    NodeTypes.REGISTRATION_NUMBER,
    NodeTypes.ADDRESS_LIST,
    NodeTypes.SHOPPING_CART,
    NodeTypes.NESTED_ROW,
    NodeTypes.MY_PRODUCTS_AND_PRICES,
    NodeTypes.QUOTE_DETAILS,
    NodeTypes.ROWS_AS_TABS,
    NodeTypes.CHECKOUT,
    NodeTypes.PORTAL_USERS_TABLE,
    NodeTypes.REGISTRATION,
    NodeTypes.RECENTLY_VIEWED_PRODUCTS,
    NodeTypes.PRODUCT_COMPARE,
    NodeTypes.LIMITED_PRODUCT_DETAILS
  ];

  static priorityElements = [NodeTypes.HOMEPAGE_SEARCH, NodeTypes.QUICK_TDS_LINK, NodeTypes.TILE, NodeTypes.MENU];

  static isGridNode(node: Node): boolean {
    return this.gridNodes.includes(node.type);
  }

  static isContainerNode(node: Node): boolean {
    return [NodeTypes.SECTION, NodeTypes.ROW, NodeTypes.COLUMN, NodeTypes.NESTED_ROW, NodeTypes.ROWS_AS_TABS, NodeTypes.NESTED_COLUMN].includes(
      node.type
    );
  }

  static isColumnNode(node: Node): boolean {
    return node.type === NodeTypes.COLUMN || node.type === NodeTypes.NESTED_COLUMN;
  }

  static isRowNode(node: Node): boolean {
    return node.type === NodeTypes.ROW || node.type === NodeTypes.NESTED_ROW;
  }

  static hasConditions(conditions: NodeCondition | NodeCondition[]): boolean {
    if (!conditions) return false;

    if (!Array.isArray(conditions)) {
      return conditions.activeConditional !== 'none';
    }

    return conditions.length > 0;
  }

  static isConditionValid(conditions: NodeCondition | NodeCondition[], locale: string, contentEntity: NormalizedEntity | null): boolean {
    if (!Array.isArray(conditions)) conditions = conditions && conditions.activeConditional !== ConditionType.NONE ? [conditions] : [];

    // Conditionals are always AND. The condition passes when all cases are true.
    return conditions.every(({ activeConditional, attributes, countries }) => {
      switch (activeConditional) {
        case ConditionType.ATTRIBUTES:
          if (!contentEntity || !('id' in contentEntity)) return false;

          return attributes && attributes.slugs.length && attributes.slugs.includes((contentEntity.id as string) ?? '');
        case ConditionType.COUNTRY:
          const currentCountry = locale?.split('-')[1]?.toUpperCase();
          const allowed = countries?.some((country) => country.isoCode.toUpperCase() === currentCountry);

          return allowed;
        default:
          return false;
      }
    });
  }
}
